import { useEffect, useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sparkles, Send, Trash2, Bot, User as UserIcon } from "lucide-react";
import { toast } from "sonner";

export default function AIChat() {
  const [sessionId, setSessionId] = useState(() => localStorage.getItem("csp_ai_session") || "");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [pending, setPending] = useState("");
  const [status, setStatus] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => { api.get("/ai/status").then((r) => setStatus(r.data)); }, []);
  useEffect(() => {
    if (!sessionId) return;
    api.get(`/ai/chat/history?session_id=${sessionId}`).then((r) => setMessages(r.data));
  }, [sessionId]);
  useEffect(() => { scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight); }, [messages, pending]);

  const send = async () => {
    if (!input.trim() || streaming) return;
    const text = input.trim();
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text, id: Date.now() }]);
    setStreaming(true);
    setPending("");
    try {
      const res = await fetch(`${API}/ai/chat/stream`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text, session_id: sessionId || null }),
      });
      const newSid = res.headers.get("X-Session-Id");
      if (newSid && !sessionId) { setSessionId(newSid); localStorage.setItem("csp_ai_session", newSid); }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value);
        // parse SSE lines: data: xxx
        for (const line of chunk.split("\n")) {
          if (line.startsWith("data: ")) {
            const tok = line.slice(6);
            if (tok === "[DONE]") continue;
            acc += tok;
            setPending(acc);
          }
        }
      }
      setMessages((m) => [...m, { role: "assistant", content: acc, id: Date.now() + 1 }]);
      setPending("");
    } catch (e) {
      toast.error("Errore streaming AI");
    } finally { setStreaming(false); }
  };

  const clearChat = async () => {
    if (!sessionId) return;
    if (!window.confirm("Cancellare tutta la conversazione?")) return;
    await api.delete(`/ai/chat/${sessionId}`);
    setMessages([]); setPending("");
    toast.success("Chat cancellata");
  };

  return (
    <div className="max-w-4xl mx-auto space-y-4 h-[calc(100vh-8rem)] flex flex-col">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-4xl font-bold tracking-tight flex items-center gap-3"><Sparkles className="w-8 h-8 text-[#0052FF]" />AI Coach</h1>
          <p className="text-zinc-500 mt-1 text-sm">
            {status?.enabled ? "Chiedi al tuo assistente tecnico." : "Assistente non disponibile al momento."}
          </p>
        </div>
        {sessionId && <Button variant="outline" onClick={clearChat} data-testid="ai-clear"><Trash2 className="w-4 h-4 mr-2" />Nuova chat</Button>}
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto no-scrollbar bg-white border border-zinc-200 rounded-md p-4 space-y-3">
        {messages.length === 0 && !pending && (
          <div className="text-center py-16 text-zinc-500 space-y-3">
            <Bot className="w-12 h-12 mx-auto text-zinc-300" />
            <div>Chiedi qualsiasi cosa sul club: pianificazione allenamenti, analisi atleti, tattica, gare.</div>
            <div className="flex flex-wrap gap-2 justify-center text-xs">
              {["Come strutturare la settimana di preparazione a una gara?", "Chi degli atleti elite ha più margini di miglioramento?", "Suggerisci 3 esercizi per le gambe-scherma under 14"].map((s) => (
                <button key={s} onClick={() => setInput(s)} className="px-3 py-2 bg-zinc-100 rounded-full hover:bg-zinc-200">{s}</button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m) => (
          <div key={m.id} className={`flex gap-3 ${m.role === "user" ? "justify-end" : ""}`}>
            {m.role === "assistant" && <div className="w-8 h-8 rounded-full bg-black text-white flex items-center justify-center flex-shrink-0"><Bot className="w-4 h-4" /></div>}
            <div className={`max-w-[75%] px-4 py-2.5 rounded-md text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-black text-white" : "bg-zinc-100 text-zinc-900"}`}>{m.content}</div>
            {m.role === "user" && <div className="w-8 h-8 rounded-full bg-zinc-200 flex items-center justify-center flex-shrink-0"><UserIcon className="w-4 h-4" /></div>}
          </div>
        ))}
        {pending && (
          <div className="flex gap-3">
            <div className="w-8 h-8 rounded-full bg-black text-white flex items-center justify-center flex-shrink-0"><Bot className="w-4 h-4" /></div>
            <div className="max-w-[75%] px-4 py-2.5 rounded-md text-sm whitespace-pre-wrap bg-zinc-100 text-zinc-900">
              {pending}<span className="animate-pulse">▍</span>
            </div>
          </div>
        )}
      </div>

      <div className="flex gap-2">
        <Input
          data-testid="ai-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder="Scrivi un messaggio…"
          disabled={streaming}
          className="h-12"
        />
        <Button data-testid="ai-send" onClick={send} disabled={streaming || !input.trim()} className="h-12 bg-black hover:bg-zinc-800">
          <Send className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}
